import React, {Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Grid,
  PAYLOAD_DATE_FORMAT,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { MutationStatus, useQueryClient } from 'react-query';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import dayjs from 'dayjs';
import RouteKey from '../../../../routes/key';
import { CandidateProfileFormData, CandidateProfileIsDisableInputs } from '../../../../models';
import { getPreferredAddress, getPreferredContactType } from '../../../../utils/ComponentUtils';
import {
  CandidateProfileDetailsResponse,
  IExamMasterCodeItem,
  LookupListApiItem,
  LookupListItem,
  UpdateCandidateProfileDetailsRequest,
} from '../../../../api/user/model';
import PersonalDetails from '../particulars/PersonalDetails';
import ContactDetails from '../particulars/ContactDetails';
import NextOfKinContactDetails from '../particulars/NextOfKinContactDetails';
import candidateProfileAEISSchema from './candidateProfileAEISSchema';
import {
  usePutCandidateProfileDetails
} from '../../../../api-refactored/user/usePutCandidateProfileDetails';
import {
  SubmitAEISAdditionalDetailsRequest,
  usePostSubmitAEISAdditionalDetails,
} from '../../../../api-refactored/non-national-exam-registration/usePostSubmitAEISAdditionalDetails';
import {
  ParentParticularsLocation
} from '../../../../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';
import {
  AEISAdditionalDetailsResponse,
  useGetAEISAdditionlDetails,
} from '../../../../api-refactored/non-national-exam-registration/useGetAEISAdditionalDetails';
import {
  IExamContentResponse,
  useGetIExamMasterCode,
} from '../../../../api-refactored/user/useGetIExamMasterCode';
import {
  useGetIntendedResidentialArea
} from '../../../../api-refactored/non-national-exam-registration/useGetIntendedResidentialArea';
import {
  Type,
  useGetWorkPassTypes,
  WorkPassType,
} from '../../../../api-refactored/candidate/useGetWorkPassTypes';
import { GET_AEIS_ADDITIONAL_DETAILS_QUERY_KEY } from '../../../../constants/queryKeys';
import { UseCustomMutationReturn } from '../../../../api-refactored/utils/useCustomMutation';
import {
  usePutCandidateProfileDetailsForOpsAdmin
} from '../../../../api-refactored/non-national-exam-registration/usePutCandidateProfileDetailsForOpsAdmin';
import { existInArray } from '../../../../utils/ArrayUtils';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import { GenericErrorResponse } from "../../../../api";

const { confirmAndPay, summary } = RouteKey.exam.child;

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  button: {
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(10),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

const userIdTypePassport = 'Passport';

const disabledInput = (isOpsAdmin: boolean): CandidateProfileIsDisableInputs => ({
  name: !isOpsAdmin,
  IdType: true,
  NricFin: !isOpsAdmin,
  finPassportNo: false,
  hanyupinyinName: false,
  gender: !isOpsAdmin,
  workPassType: false,
  dateOfBirth: !isOpsAdmin,
  race: !isOpsAdmin,
  secondaryRace: true,
  nationality: !isOpsAdmin,
  prStatus: true,
  nationalityIssueDate: false,
  citizenship: true,
  occupation: true,
  school: false,
  houseOrBlkNo: false,
  streetName: false,
  postalCode: false,
  intendedResidentialArea: false,
});

const updateCandidateDetails = (
  formData: CandidateProfileFormData,
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>,
  formDefaultValues: CandidateProfileFormData | undefined,
  updateCandidateProfileMutation: UseCustomMutationReturn<
    void,
    AxiosError,
    CandidateProfileDetailsResponse,
    unknown
  >,
  updateAdditionalDetailsMutation: UseCustomMutationReturn<
    void,
    AxiosError,
    SubmitAEISAdditionalDetailsRequest,
    unknown
  >,
  nextPage: () => void,
  lockedByUser?: string
) => {
  if (lockedByUser) {
    nextPage();
    return;
  }

  updateAdditionalDetailsMutation.reset();
  updateCandidateProfileMutation.reset();
  setApiButtonStatus('loading');
  const putRequestBody: UpdateCandidateProfileDetailsRequest = {
    statutoryName: formData.name,
    candidateId: formData.NricFin,
    userIdType: formDefaultValues?.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
    passportNo: formData.finPassportNo,
    hanyuPinyinName: formData.hanyupinyinName,
    email: formData.email,
    citizenship: formDefaultValues?.citizenship,
    nationality: formData.nationality,
    nationalityIssueDate: formData.nationalityIssueDate
      ? dayjs(formData.nationalityIssueDate).format(PAYLOAD_DATE_FORMAT)
      : '',
    dateOfBirth: dayjs(
      formData.dateOfBirth ? formData.dateOfBirth : formDefaultValues?.dateOfBirth
    ).format(PAYLOAD_DATE_FORMAT),
    prStatus: formDefaultValues?.prStatus,
    gender: formData.gender,
    race: formData.race,
    occupation: null,
    // It seems that school field details won't be affected no matter what non-empty code or description is defined.
    school: { code: '1', description: '2' },

    // addresses
    residentialAddress: {
      houseOrBlockNumber: formData.houseOrBlkNo,
      floorNumber: formData.floor,
      unitNumber: formData.unit,
      buildingName: formData.building,
      streetName: formData.streetName,
      postalCode: formData.postalCode,
    },
    foreignAddress1: formData.foreignAddress1,
    foreignAddress2: formData.foreignAddress2,
    foreignAddress3: formData.foreignAddress3,
    localAddressPreferred: formData.preferredAddress === 'L',

    // contact numbers
    mobileNumber: {
      countryCode: formData.mobileCountryCode,
      number: formData.mobileContactNumber,
    },
    telephoneNumber: {
      countryCode: formData.telephoneCountryCode,
      number: formData.telephoneContactNumber,
    },
    mobileNumberPreferred: formData.preferredTelephoneOrMobile === 'M',

    // next of kin
    nextOfKinName: formData.nextOfKinName,
    nextOfKinContactNumber: {
      countryCode: formData.nextOfKinCountryCode,
      number: formData.nextOfKinContactNumber,
    },
    nextOfKinRelationship: formData.nextOfKinRelationshipToCandidate,
  };
  updateCandidateProfileMutation.mutate(putRequestBody);
};

const getFormDefaultValuesObject = (
  profileData: AEISAdditionalDetailsResponse,
  iExamsData: IExamContentResponse,
  workPassTypes: WorkPassType[],
  residentialAreas: LookupListApiItem[]
): CandidateProfileFormData => ({
  name: profileData.name,
  hanyupinyinName: profileData.hanyuPinyinName,
  IdType: profileData.userIdType === 'NRIC/FIN' ? 'I' : 'P',
  NricFin: profileData.nricFinPassport,
  dateOfBirth: profileData.dob,
  email: profileData.email,
  gender: iExamsData?.gender.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.gender.toUpperCase()
  ) as IExamMasterCodeItem,
  race: iExamsData?.race.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.race.toUpperCase()
  ) as IExamMasterCodeItem,
  citizenship: iExamsData?.citizenship.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.citizenship
  ) as IExamMasterCodeItem,
  nationality: iExamsData?.nationality.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.nationality
  ) as IExamMasterCodeItem,
  nationalityIssueDate: profileData.nationalityIssueDate,
  prStatus: iExamsData?.prStatus.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.prStatus
  ) as IExamMasterCodeItem,
  // addresses
  houseOrBlkNo: profileData.mailingAddress?.houseOrBlockNumber,
  streetName: profileData.mailingAddress?.streetName,
  building: profileData.mailingAddress?.buildingName,
  floor: profileData.mailingAddress?.floorNumber,
  unit: profileData.mailingAddress?.unitNumber,
  postalCode: profileData.mailingAddress?.postalCode,
  foreignAddress1: profileData.foreignAddress1,
  foreignAddress2: profileData.foreignAddress2,
  foreignAddress3: profileData.foreignAddress3,
  preferredAddress: getPreferredAddress(profileData.localAddressPreferred),
  school: { id: 0, name: profileData.schoolName } as LookupListItem,
  // contact numbers
  telephoneCountryCode: (profileData.phoneNumber?.countryCode as IExamMasterCodeItem) ?? null,
  telephoneContactNumber: profileData.phoneNumber?.number ?? null,  
  mobileCountryCode: profileData.mobileNumber?.countryCode as IExamMasterCodeItem,
  mobileContactNumber: profileData.mobileNumber?.number,
  preferredTelephoneOrMobile: getPreferredContactType(profileData.mobileNumberPreferred),

  // next of kin
  nextOfKinName: profileData.nextOfKinName,
  nextOfKinCountryCode: profileData.nextOfKin?.countryCode as IExamMasterCodeItem,
  nextOfKinContactNumber: profileData.nextOfKin?.number,
  nextOfKinRelationshipToCandidate: iExamsData?.relationship.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.nextOfKinRelationship
  ) as IExamMasterCodeItem,

  // additional AEIS details
  workPassType: workPassTypes?.find(
    ({ code }) => code === profileData.workPassType
  ) as LookupListApiItem,
  intendedResidentialArea: residentialAreas?.find(
    ({ code }) => code === profileData.intendedResidentialArea
  ) as LookupListApiItem,
  finPassportNo: profileData.finPassport || '',
});

const onErrorHandler = (
  err: AxiosError<GenericErrorResponse>,
  t: TFunction,
  setErrorMessage: Dispatch<SetStateAction<string>>,
  setApiButtonStatus: Dispatch<SetStateAction<MutationStatus>>
) => {
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) {
    setErrorMessage(serverMessage);
  } else {
    setErrorMessage(t('components->>Error.DefaultErrorMsg'));
  }
  setApiButtonStatus('error');
};

const ConfirmParticularsAEIS = ({ isOpsAdmin }: { isOpsAdmin: boolean }) => {
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const { state } = useLocation<ParentParticularsLocation>();
  const { t } = useTranslation();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [isSettingUpFormValue, setIsSettingUpFormValue] = useState(true);
  const queryClient = useQueryClient();
  const onBack = () => {
    history.goBack();
  };

  const { data: profileData, isFetching: isFetchingProfileData } = useGetAEISAdditionlDetails({
    uuid: applicationUuid,
  });
  const { data: iExamsData } = useGetIExamMasterCode();
  const { data: residentialAreas } = useGetIntendedResidentialArea();
  const { data: workPassTypes } = useGetWorkPassTypes({
    type: Type.candidate,
  });

  const formDefaultValues: CandidateProfileFormData | undefined = useMemo(() => {
    if (!profileData || !workPassTypes || !residentialAreas || !iExamsData) {
      return undefined;
    }
    return getFormDefaultValuesObject(profileData, iExamsData, workPassTypes, residentialAreas);
  }, [profileData, iExamsData, workPassTypes, residentialAreas]);

  useEffect(() => {
    if (formDefaultValues) {
      form.reset(formDefaultValues);
      setIsSettingUpFormValue(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefaultValues]);

  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;

  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;

  const form = useForm<CandidateProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      candidateProfileAEISSchema(
        disabledInput(isOpsAdmin),
        'Next-of-kin Name',
        'Next-of-kin Contact No.',
        profileData?.userIdType === userIdTypePassport
      )
    ),
    defaultValues: formDefaultValues,
  });

  const updateCandidateProfileMutation = usePutCandidateProfileDetails({
    onSuccess: () => updateAdditionalDetails(),
    onError: (err) => onErrorHandler(err, t, setErrorMessage, setApiButtonStatus),
  });
  const opsAdminUpdateProfileMutation = usePutCandidateProfileDetailsForOpsAdmin({
    uuid: applicationUuid,
    onSuccess: () => updateAdditionalDetails(),
    onError: (err) => onErrorHandler(err, t, setErrorMessage, setApiButtonStatus),
  });
  const updateAdditionalDetailsMutation = usePostSubmitAEISAdditionalDetails({
    uuid: applicationUuid,
    onSuccess: () => {
      queryClient.invalidateQueries(GET_AEIS_ADDITIONAL_DETAILS_QUERY_KEY);
      nextPage();
    },
    onError: (err: AxiosError<GenericErrorResponse>) => {
      if (err?.response?.data.message === 'school detail not found.') {
        setErrorMessage(t('exam->>NonNationalExam.EnterSchoolingDetails'));
      } else if (err?.response?.data.code === '1022') {
        setErrorMessage(err?.response?.data.message!);
      } else {
        setErrorMessage(t('components->>Error.DefaultErrorMsg'));
      }
      setApiButtonStatus('error');
    },
  });

  const updateAdditionalDetails = () => {
    const AEISAdditionalDetailsRequestBody = {
      intendedResidentialArea: form.getValues("intendedResidentialArea")?.code || '',
      workPassType: form.getValues("workPassType")?.code || '',
      finPassport: form.getValues("finPassportNo") || '',
      nricFinPassport: form.getValues("NricFin") || '',
    };
    updateAdditionalDetailsMutation.mutate(AEISAdditionalDetailsRequestBody);
  };

  const nextPage = () =>
    history.push(isOpsAdmin ? `${summary.route}` : `${confirmAndPay.route}`, state);

  return (
    <>
      {isSettingUpFormValue || isFetchingProfileData || !profileData ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <Box>
          <form
            onSubmit={form.handleSubmit(
              (formData) => {
                setErrorMessage('');
                updateCandidateDetails(
                  formData,
                  setApiButtonStatus,
                  formDefaultValues,
                  isOpsAdmin ? opsAdminUpdateProfileMutation : updateCandidateProfileMutation,
                  updateAdditionalDetailsMutation,
                  nextPage,
                  profileData?.lockedByUser
                );
              },
              () => {
                if (!hasEditAccess && hasViewAccess) {
                  nextPage();
                }
              }
            )}
          >
            <ContentTemplate isContinuable type="full">
              <SectionHeader title={t('exam->>NonNationalExam.ConfirmParticulars')} />
              <PersonalDetails
                sameRowName
                examCandidateDetail={profileData}
                isEditState={hasEditAccess}
                isAEIS
                isOpsAdmin={isOpsAdmin}
                form={form}
                defaultExpanded
              />
              <ContactDetails
                contactDetails={profileData}
                formDefaultValues={formDefaultValues}
                sameRowAddress
                sameRowPhone
                isEditState={hasEditAccess}
                isAEIS
                form={form}
                defaultExpanded
              />
              <NextOfKinContactDetails
                nextOfKinName={profileData?.nextOfKinName}
                nextOfKinRelationship={profileData?.nextOfKinRelationship}
                form={form}
                formDefaultValues={formDefaultValues}
                accordionBottomLine={false}
                isEditState={hasEditAccess}
                isLastItem
                defaultExpanded
              />
            </ContentTemplate>
            <ContentTemplate continuous>
              {errorMessage && (
                <Typography variant="body2" className={classes.errString}>
                  {errorMessage}
                </Typography>
              )}
              <Box className={classes.buttonContainer}>
                {profileData?.lockedByUser && (
                  <Typography variant="body2" className={classes.errString}>
                    {t('exam->>NonNationalExam.LockedMessage', {
                      lockedByUser: profileData?.lockedByUser,
                    })}
                  </Typography>
                )}
                <Grid container direction="row" alignItems="flex-start" spacing={3}>
                  <Grid xs={12} md={3}>
                    <Button
                      id="button-confirmParticulars-back"
                      fullWidth
                      className={classes.button}
                      variant="outlined"
                      onClick={onBack}
                    >
                      {t('exam->>ReviewExam.Back')}
                    </Button>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <ApiButton
                      id="button-confirmParticulars-confirm"
                      fullWidth
                      type="submit"
                      status={apiButtonStatus}
                      className={classes.button}
                      variant="contained"
                      disabled={apiButtonStatus === 'loading'}
                    >
                      {t('exam->>ReviewExam.Confirm Particulars')}
                    </ApiButton>
                  </Grid>
                </Grid>
              </Box>
            </ContentTemplate>
          </form>
        </Box>
      )}
    </>
  );
};
export default ConfirmParticularsAEIS;
